import { useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { DocumentType } from "@/shared/types/documentTypes";

import type { LegalRole } from "@/shared/hooks/useUserEstateRole";

const LETTERS_DOC_TYPES: string[] = [
  DocumentType.LETTERS_TESTAMENTARY,
  DocumentType.LETTERS_ADMINISTRATION,
];

const TRUST_DOC_TYPES: string[] = [
  DocumentType.TRUST,
  DocumentType.CERTIFICATION_OF_TRUST,
];

function docTypesForRole(legalRole: LegalRole): string[] {
  if (legalRole === "trustee") {
    return TRUST_DOC_TYPES;
  }
  if (legalRole === "executor" || legalRole === "administrator") {
    return LETTERS_DOC_TYPES;
  }
  return [];
}

function buildQueryKey(estateId: number | undefined, legalRole?: LegalRole) {
  return ["hasAuthorityDocument", estateId, legalRole ?? "letters"] as const;
}

export function useHasAuthorityDocument(estateId: number | undefined): {
  hasAuthorityDocument: boolean;
  isFetched: boolean;
  setHasAuthorityDocument: () => void;
};
export function useHasAuthorityDocument(
  estateId: number | undefined,
  legalRole: LegalRole,
): {
  hasAuthorityDocument: boolean;
  isFetched: boolean;
  setHasAuthorityDocument: () => void;
};
export function useHasAuthorityDocument(
  estateId: number | undefined,
  legalRole?: LegalRole,
) {
  const queryClient = useQueryClient();

  const docTypes =
    legalRole !== undefined ? docTypesForRole(legalRole) : LETTERS_DOC_TYPES;

  const { data: hasAuthorityDocument = false, isFetched } = useQuery({
    queryKey: buildQueryKey(estateId, legalRole),
    queryFn: async () => {
      if (docTypes.length === 0) {
        return false;
      }

      const { data, error } = await supabase
        .from("estate_documents")
        .select("id")
        .eq("estate_id", estateId!)
        .in("document_type", docTypes)
        .limit(1);

      if (error) throw error;

      return !!data && data.length > 0;
    },
    enabled: !!estateId && docTypes.length > 0,
  });

  const setHasAuthorityDocument = () => {
    queryClient.setQueryData(buildQueryKey(estateId, legalRole), true);
  };

  return {
    hasAuthorityDocument,
    isFetched: !estateId || docTypes.length === 0 || isFetched,
    setHasAuthorityDocument,
  };
}
